// -----------------------------------------------------------------------------
//
// This file is the copyrighted property of Tableau Software and is protected
// by registered patents and other applicable U.S. and international laws and
// regulations.
//
// Unlicensed use of the contents of this file is prohibited. Please refer to
// the NOTICES.txt file for further details.
//
// -----------------------------------------------------------------------------

import { all, call, put } from 'redux-saga/effects';
import { Messages } from '../../../../codegen/Localize';
import { CommonErrorCode, ExtractCreationErrorCode } from '../../../../ts/helpers/ErrorCodes';
import { DatasourceContentType, WorkbookContentType } from '../../../../ts/models/ContentTypes';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IErrorItem, MultipleItemResult } from '../../../../ts/models/MultipleItemResult';
import { Set } from '../../../../ts/models/Set';
import { DataSources } from '../../../../ts/resources/Datasources';
import { Workbooks } from '../../../../ts/resources/Workbooks';
import { IResult } from '../../../../ts/ServerApi/types/IResponse';
import { ContentActionNotification, IMultipleResultNotificationOptions, MultipleResultNotificationOptions } from '../../../../ts/services/ContentActionNotification';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { CancelExtractTaskAction  } from '../../../actions/spaces/CancelExtractTaskAction';
import { EncryptionActionType } from '../../../actions/spaces/EncryptContentAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { ServiceLocator } from '../../../ServiceLocator';
import { replaceDatasourceIdWithConnectionIdForMixedContent } from '../SpacesSagaHelpers';
import { encryptDataSources, encryptWorkbooks, processResult, showNotification } from './EncryptContentSaga';
import { reloadSpaceIfUpdatingCurrentSpace } from './RefreshContentSagas';
import { SagaIterator } from 'redux-saga';

export function cancelExtractCreationOnDatasource(datasources: Set<IContentInfo>): ng.IPromise<IResult> {
  if(datasources.size() < 1) {
    return null;
  }
  const resource: DataSources = ServiceLocator.get<DataSources>(DataSources.ServiceLocatorKey);
  return resource.cancelExtractCreationOnDatasources(datasources.map(datasource => datasource.id));
}

export function cancelExtractCreationOnWorkbook(workbooks: Set<IContentInfo>): ng.IPromise<IResult> {
  if(workbooks.size() < 1) {
    return null;
  }
  const resource: Workbooks = ServiceLocator.get<Workbooks>('Workbooks');
  return resource.cancelExtractCreationOnWorkbooks(workbooks.map(workbook => workbook.id));
}

export function cancelExtractEncryptionOnDatasource(datasources: Set<IContentInfo>): ng.IPromise<IResult> {
  if(datasources.size() < 1) {
    return null;
  }
  const resource: DataSources = ServiceLocator.get<DataSources>(DataSources.ServiceLocatorKey);
  return resource.cancelExtractCreationOnDatasources(datasources.map(datasource => datasource.id));
}

export function cancelExtractEncryptionOnWorkbook(datasources: Set<IContentInfo>): ng.IPromise<IResult> {
  if(datasources.size() < 1) {
    return null;
  }
  const resource: DataSources = ServiceLocator.get<DataSources>(DataSources.ServiceLocatorKey);
  return resource.cancelExtractCreationOnDatasources(datasources.map(datasource => datasource.id));
}

export function processCancelExtractCreationResult(dataSourcesResult: IResult, workbooksResult: IResult): IResult {
  let result: IResult;
  const errors = [];
  if(dataSourcesResult != null) {
    errors.push(...dataSourcesResult.errors);
  }

  if(workbooksResult != null) {
    errors.push(...workbooksResult.errors);
  }

  result = {
    errors: errors
  };
  return result;
}

export function showExtractCreationNotification(result: IResult, content: IContentInfo[]) {
  const notification: IMultipleResultNotificationOptions = MultipleResultNotificationOptions.create(
    new MultipleItemResult(Set.fromIdItemArray(content), result),
    {
      describeSuccessSummary: () => {
        return Messages.cancelExtractCreationSuccessful();
      },
      describeErrorSummary: () => {
        return Messages.cancelExtractCreationFailed();
      },
      describeErrorDetail: (errorItem: IErrorItem<IContentInfo>) => {
        return {
          itemName: errorItem.item.name,
          errorMessage: getErrorMessageFor(errorItem.error.code)
        };
      }
    }
  );
  const contentActionNotification: ContentActionNotification = ServiceLocator.get<ContentActionNotification>('ContentActionNotification');
  contentActionNotification.notify(notification);
}

export function getErrorMessageFor(errorCode: number): string {
  switch (errorCode) {
    case CommonErrorCode.CallingUserInsufficientPermissions:
      return Messages.permission_denied();
    case CommonErrorCode.UnexpectedErrorOccurred:
        return Messages.cancelExtractCreationFailed();
    case ExtractCreationErrorCode.BackgroundJobNotFound:
      return Messages.cancelExtractCreationFailedNotFound();
    default:
      return Messages.errorUnknown();
  }
}

export function* processExtractContentAction(action: CancelExtractTaskAction): SagaIterator {
  try {
    const mutableContentCopy: IContentInfo[] = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, action.contentArray);
    const datasources: Set<IContentInfo> = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === DatasourceContentType.name));
    const workbooks: Set<IContentInfo> = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === WorkbookContentType.name));
    if (action.cancelCreation) {
      const [dataSourcesResult, workbooksResult] = (yield all([
        call(cancelExtractCreationOnDatasource, datasources),
        call(cancelExtractCreationOnWorkbook, workbooks),
      ])) as any; // TFS 1116084 - need to upgrade redux-saga in order to remove 'as any'
      const result = processCancelExtractCreationResult(dataSourcesResult, workbooksResult);
      yield call(showExtractCreationNotification, result, mutableContentCopy);
    } else {
      const encryptionActionType = action.encryptionActionType ? action.encryptionActionType : EncryptionActionType.decryptExtracts;
      const [dataSourcesResult, workbooksResult] = (yield all([
        call(encryptDataSources, datasources, encryptionActionType),
        call(encryptWorkbooks, workbooks, encryptionActionType),
      ])) as any; // TFS 1116084 - need to upgrade redux-saga in order to remove 'as any'
      const result = processResult(dataSourcesResult, workbooksResult, null);
      yield call(showNotification, result, mutableContentCopy, encryptionActionType);
    }
    const didReload = yield call(reloadSpaceIfUpdatingCurrentSpace, action.contentArray);
    if (!didReload) {
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}
